// File: src/runtime/npc/npcRuntimeLoop.ts
//
// NPC Runtime Loop
// - Reads timeline state (playheadSec + ranges)
// - Computes npc runtime snapshot
// - Publishes via a single canonical event: "omnicine:npc:runtime"
// - Optional publish() callback for local consumers
//
// Emits:
//   window.dispatchEvent(new CustomEvent("omnicine:npc:runtime", { detail: snapshot }))
//
// Debug mirror:
//   window.__oc_npc_runtime = snapshot
//
// A3-safe: no reducer writes.

import {
  computeNpcRuntime,
  type NpcRuntimeSnapshot,
  type TimelineRangeLike,
} from "@/runtime/npc/npcStateMachine";

export type NpcRuntimeLoopOptions = {
  /**
   * If omitted, defaults to window.__oc_debug.getState()
   */
  getState?: () => any;

  /**
   * Loop cadence in milliseconds.
   * Default: 50ms (~20Hz)
   */
  cadenceMs?: number;

  /**
   * NPC lane/track identifier used by the runtime machine.
   * Default: "n1"
   */
  npcTrackId?: string;

  /**
   * Influence window in seconds around playhead.
   * Default: 0.25s
   */
  windowSec?: number;

  /**
   * Optional sink for consumers (HUD / BLE / OmniCast bridge).
   * Called only when a de-duped snapshot changes.
   */
  publish?: (snapshot: NpcRuntimeSnapshot) => void;

  /**
   * Optional audit annotation (non-breaking).
   */
  source?: "user" | "xr-user" | "ai-assist" | "system";
};

const EVENT_NAME = "omnicine:npc:runtime";

function toNum(v: unknown, fallback = 0): number {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function readPlayheadSec(st: any): number {
  return toNum(st?.playheadSec ?? st?.timeline?.playheadSec, 0);
}

function readRanges(st: any): TimelineRangeLike[] {
  const r = st?.ranges ?? st?.timeline?.ranges;
  return Array.isArray(r) ? (r as TimelineRangeLike[]) : [];
}

function shallowSig(s: NpcRuntimeSnapshot): string {
  // Dedupe signature: mode + probs rounded + active ranges.
  const p = s.probs;
  const r3 = (x: number) => Math.round(x * 1000) / 1000;

  return [
    s.mode,
    r3(p.attention),
    r3(p.emotion),
    r3(p.overload),
    r3(p.safety),
    (s.activeRangeIds ?? []).join(","),
  ].join("|");
}

/**
 * startNpcRuntimeLoop
 * -------------------
 * Starts a timer loop that reads state and emits a de-duped snapshot.
 *
 * Returns:
 *   stop() cleanup function.
 */
export function startNpcRuntimeLoop(opts: NpcRuntimeLoopOptions = {}): () => void {
  const options: NpcRuntimeLoopOptions = opts ?? {};

  const getState =
    options.getState ??
    (() => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const w: any = window as any;
      return w.__oc_debug?.getState?.();
    });

  const cadenceMs = Math.max(16, toNum(options.cadenceMs, 50));
  const npcTrackId = String(options.npcTrackId ?? "n1");
  const windowSec = Math.max(0, toNum(options.windowSec, 0.25));
  const publish = options.publish;

  let alive = true;
  let timer: number | null = null;
  let lastSig = "";

  const emit = (snapshot: NpcRuntimeSnapshot) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const w: any = window as any;
    w.__oc_npc_runtime = snapshot;

    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: snapshot }));
    publish?.(snapshot);
  };

  const tick = () => {
    if (!alive) return;

    let st: any = null;
    try {
      st = getState?.();
    } catch {
      st = null;
    }

    if (st) {
      const playheadSec = readPlayheadSec(st);
      const ranges = readRanges(st);

      const snapshot = computeNpcRuntime({
        playheadSec,
        ranges,
        npcTrackId,
        windowSec,
      });

      const sig = shallowSig(snapshot);
      if (sig !== lastSig) {
        lastSig = sig;
        emit(snapshot);
      }
    }

    schedule();
  };

  const schedule = () => {
    if (!alive) return;
    timer = window.setTimeout(tick, cadenceMs) as unknown as number;
  };

  // Warm start: schedule immediately (and let dedupe suppress repeats).
  schedule();

  return function stopNpcRuntimeLoop() {
    alive = false;
    if (timer != null) window.clearTimeout(timer);
    timer = null;
  };
}

/**
 * Compatibility default export (harmless if unused).
 * Keeps ESM export shape stable across refactors / imports.
 */
export default startNpcRuntimeLoop;
